import { useContext, useMemo } from "react";
import { AuthContext } from "../contexts/JWTContext";
import { isValidToken } from "../utils/jwt";
import usePermissions from "./usePermissions";

function hasStoredSessionToken() {
  const accessToken = window.localStorage.getItem("accessToken");
  const accessSuperToken = window.localStorage.getItem("accessSuperToken");
  return (
    (accessToken && isValidToken(accessToken)) ||
    (accessSuperToken && isValidToken(accessSuperToken))
  );
}

/**
 * Resolves permission gate state for v2 pages: loading while auth hydrates,
 * then allowed or denied (avoids Access Denied flash on refresh).
 */
export default function usePermissionGate({
  permission = null,
  permissions = [],
  requireAll = false,
  resource = null,
  action = null,
  allowAdmin = true,
} = {}) {
  const authContext = useContext(AuthContext);
  const { isInitialized, user, sauser } = authContext || {};
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasResourcePermission,
    isAdmin,
  } = usePermissions();

  const pendingHydration = useMemo(() => {
    const hasToken = hasStoredSessionToken();
    const authReady = isInitialized || !!user || !!sauser;
    return hasToken && !authReady;
  }, [isInitialized, user, sauser]);

  const hasAccess = useMemo(() => {
    if (pendingHydration) return false;
    if (allowAdmin && isAdmin()) return true;
    if (resource && action) return hasResourcePermission(resource, action);
    if (Array.isArray(permissions) && permissions.length > 0) {
      return requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
    }
    if (typeof permission === "string") return hasPermission(permission);
    return true;
  }, [
    pendingHydration,
    allowAdmin,
    isAdmin,
    resource,
    action,
    permissions,
    requireAll,
    permission,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasResourcePermission,
  ]);

  const status = pendingHydration ? "loading" : hasAccess ? "allowed" : "denied";

  return { status, hasAccess, pendingHydration };
}
